import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, CATEGORY, errMsg, PRIORITY, uploadFiles } from "@/lib/api";
import { PageHeader } from "@/components/bits";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { ImageIcon, Loader2, Paperclip, X } from "lucide-react";
import { toast } from "sonner";

export default function StaffNewTicketPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [form, setForm] = useState({ title: "", description: "", category: "outros", priority: "media", client_id: "", area_id: "" });
  const [clients, setClients] = useState([]);
  const [areas, setAreas] = useState([]);
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/tenant/users")
      .then(({ data }) => setClients(data.filter((u) => u.role === "client" && u.active)))
      .catch(() => {});
    api.get("/tenant/areas").then(({ data }) => setAreas(data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const attachments = await uploadFiles(files);
      const payload = { ...form, attachments };
      if (!payload.client_id) delete payload.client_id;
      if (!payload.area_id) delete payload.area_id;
      const { data } = await api.post("/tickets", payload);
      toast.success(`Chamado #${String(data.number).padStart(4, "0")} aberto com sucesso`);
      navigate(`/app/chamados/${data.id}`);
    } catch (err) {
      toast.error(errMsg(err));
      setSaving(false);
    }
  };

  return (
    <div data-testid="staff-new-ticket-page" className="max-w-2xl">
      <PageHeader title="Novo Chamado" subtitle="Registre um chamado para um cliente ou para a operação" />
      <form onSubmit={submit} onKeyDown={(e) => { if (e.key === "Enter" && e.target.tagName === "INPUT") e.preventDefault(); }}
        className="bg-zinc-900 border border-zinc-800 rounded-md p-6 space-y-5" data-testid="staff-new-ticket-form">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs text-zinc-400">Cliente (opcional)</Label>
            <Select value={form.client_id || "none"} onValueChange={(v) => setForm({ ...form, client_id: v === "none" ? "" : v })}>
              <SelectTrigger data-testid="ticket-client-select" className="mt-1.5 bg-zinc-950 border-zinc-800 text-zinc-200 rounded-md">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-200">
                <SelectItem value="none">Abrir em meu nome</SelectItem>
                {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} — {c.email}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {(user?.sso_origin_name || user?.sso_external_ref) && (
            <div data-testid="sso-origin-info" className="bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2 self-end">
              {user?.sso_origin_name && (
                <p className="text-xs text-zinc-400">Nome do cliente: <span className="text-zinc-100 font-medium">{user.sso_origin_name}</span></p>
              )}
              {user?.sso_external_ref && (
                <p className="text-xs text-zinc-400">Origem: <span className="text-zinc-100 font-medium">{user.sso_external_ref}</span></p>
              )}
            </div>
          )}
        </div>
        <div>
          <Label className="text-xs text-zinc-400">Local (opcional)</Label>
          <Select value={form.area_id || "none"} onValueChange={(v) => setForm({ ...form, area_id: v === "none" ? "" : v })}>
            <SelectTrigger data-testid="ticket-area-select" className="mt-1.5 bg-zinc-950 border-zinc-800 text-zinc-200 rounded-md">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-200">
              <SelectItem value="none">Em branco (sem local)</SelectItem>
              {areas.map((a) => <SelectItem key={a.id} value={a.id}>{a.code} — {a.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-zinc-400">Título</Label>
          <Input data-testid="ticket-title-input" required value={form.title} maxLength={120}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="Ex.: Impressora fiscal com erro de comunicação"
            className="mt-1.5 bg-zinc-950 border-zinc-800 text-zinc-100 placeholder:text-zinc-500 rounded-md focus:border-indigo-500" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs text-zinc-400">Categoria</Label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger data-testid="ticket-category-select" className="mt-1.5 bg-zinc-950 border-zinc-800 text-zinc-200 rounded-md">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-200">
                {Object.entries(CATEGORY).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Prioridade</Label>
            <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
              <SelectTrigger data-testid="ticket-priority-select" className="mt-1.5 bg-zinc-950 border-zinc-800 text-zinc-200 rounded-md">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-200">
                {Object.entries(PRIORITY).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label className="text-xs text-zinc-400">Descrição detalhada</Label>
          <Textarea data-testid="ticket-description-input" required value={form.description} rows={5}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Descreva o problema relatado…"
            className="mt-1.5 bg-zinc-950 border-zinc-800 text-zinc-100 placeholder:text-zinc-500 rounded-md focus:border-indigo-500" />
        </div>
        <div>
          <Label className="text-xs text-zinc-400">Anexos (até 5)</Label>
          <div className="mt-1.5">
            <input ref={fileRef} type="file" accept="image/*,application/pdf" multiple hidden data-testid="ticket-file-input"
              onChange={(e) => setFiles([...files, ...Array.from(e.target.files)].slice(0, 5))} />
            <Button type="button" data-testid="ticket-attach-button" variant="outline" onClick={() => fileRef.current?.click()}
              className="bg-transparent border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-md">
              <Paperclip className="h-4 w-4 mr-1.5" /> Adicionar anexos
            </Button>
            {files.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {files.map((f, i) => (
                  <span key={i} className="flex items-center gap-1.5 text-xs bg-zinc-800 text-zinc-300 rounded-md px-2 py-1">
                    <ImageIcon className="h-3 w-3" /> {f.name}
                    <button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))} className="text-zinc-500 hover:text-zinc-200">
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={() => navigate("/app/chamados")}
            className="text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 rounded-md">Cancelar</Button>
          <Button data-testid="ticket-submit-button" type="submit" disabled={saving}
            className="bg-indigo-600 hover:bg-indigo-500 text-white rounded-md transition-colors duration-200">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Abrir Chamado"}
          </Button>
        </div>
      </form>
    </div>
  );
}
